<script setup lang="ts">
import { MapPin as MapPinIcon } from '@lucide/vue'
import { computed } from 'vue'

import BaseButton from '@/components/ui/BaseButton.vue'
import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'
import { usePoiBrowse } from '@/composables/usePoiBrowse'
import { POI_BROWSE_GROUPS, type PoiBrowseCategory } from '@/config/poiBrowseCategories'
import { getPoiColor } from '@/services/poiService'
import { useWorkspaceStore } from '@/stores/workspaceStore'

/**
 * gpx.studio / Komoot-style POI category picker. Available everywhere (Map, Library, Collections
 * overview, and inside a collection). Turning a category on renders its POIs on the map (drawn from the
 * route.camp vector basemap via `PoiBrowseLayer`, so it works offline). With a collection open, tapping
 * a rendered POI shows a card to add it; elsewhere it's view-only (find Water, a Café…). The icon chip
 * uses the category colour (matching the candidate map markers) so the list doubles as a colour legend.
 */
defineOptions({ name: 'PoiCategoryBrowser' })

const { isCategoryOn, setCategory, clearAll, selectedCount } = usePoiBrowse()
const workspaceStore = useWorkspaceStore()

// Only inside an open collection can a tapped POI be added (to the collection or a route waypoint).
const canAdd = computed(() => !!workspaceStore.clientId)

const categoryColor = (category: PoiBrowseCategory) => getPoiColor(category.poiType)
</script>

<template>
  <div class="px-1 pb-2" data-testid="poi-category-browser">
    <!-- Pinned header: a left column (title + helper text) and a right column (Clear button), in one
         fixed element that stays put while the categories scroll. Two columns so the helper text wraps
         within its own column instead of flowing under the button. -->
    <div
      class="sticky top-0 z-10 -mx-1 flex items-start justify-between gap-3 border-b border-gray-100 bg-white px-3 pt-2.5 pb-3"
    >
      <div class="min-w-0">
        <p class="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
          <MapPinIcon class="h-4 w-4 shrink-0 text-emerald-600" />
          Points of interest
        </p>
        <p class="mt-1.5 text-xs text-gray-500">
          {{
            canAdd
              ? 'Turn on categories, then tap a pin on the map to add it to this collection.'
              : 'Turn on categories to show them on the map — find water, a café and more.'
          }}
        </p>
      </div>
      <div v-if="selectedCount > 0" class="shrink-0">
        <BaseButton variant="primary" size="sm" @click="clearAll">
          Clear
          <span
            class="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-white/25 px-1 text-[11px] font-semibold text-white tabular-nums"
          >
            {{ selectedCount }}
          </span>
        </BaseButton>
      </div>
    </div>

    <div v-for="group in POI_BROWSE_GROUPS" :key="group.id" class="border-t border-gray-100 py-1.5">
      <p class="px-2 py-1 text-[11px] font-medium uppercase tracking-[0.12em] text-gray-400">
        {{ group.label }}
      </p>
      <button
        v-for="category in group.categories"
        :key="category.id"
        type="button"
        class="flex min-h-11 w-full items-center justify-between rounded-md px-2 py-1.5 text-left transition hover:bg-emerald-50/50 hover:cursor-pointer"
        @click="setCategory(category, !isCategoryOn(category))"
      >
        <span class="flex min-w-0 items-center gap-2.5">
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            :class="isCategoryOn(category) ? 'text-white' : 'bg-gray-100 text-gray-500'"
            :style="
              isCategoryOn(category) ? { backgroundColor: categoryColor(category) } : undefined
            "
          >
            <component :is="category.icon" class="h-4 w-4" />
          </span>
          <span class="truncate text-sm text-gray-800">{{ category.label }}</span>
        </span>
        <BaseCheckbox
          :model-value="isCategoryOn(category)"
          :aria-label="`Toggle ${category.label}`"
          @click.stop
          @update:model-value="(checked: boolean) => setCategory(category, checked)"
        />
      </button>
    </div>
  </div>
</template>
